import React from "react";

import { BrandMarkSpinner } from "@/components/workspace/brand-mark-spinner";
import { HubListChromeSkeleton } from "@/components/workspace/ListTableSkeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "@/lib/hooks/use-translation";
import { cn } from "@/lib/utils";

export interface LoadingSpinnerProps {
  size?: "sm" | "lg";
  label?: string;
  className?: string;
}

export function PageLoadingFallback({ testId = "page-loading-spinner" }: { testId?: string }) {
  const { t } = useTranslation();
  return (
    <div
      className="relative flex min-h-screen w-full flex-col items-center justify-center overflow-hidden bg-background"
      role="status"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_42%,rgb(var(--nice-gold-rgb)/0.12),transparent_52%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-muted/25 to-transparent"
      />
      <div className="relative flex flex-col items-center gap-5 px-6">
        <BrandMarkSpinner size="xl" testId={testId} />
        <div className="relative h-0.5 w-44 overflow-hidden rounded-full bg-muted/70 sm:w-52" aria-hidden>
          <div className="dashboard-refresh-rail-bar absolute inset-y-0 w-1/3 rounded-full bg-[rgb(var(--nice-gold-rgb)/0.85)]" />
        </div>
        <div className="space-y-1 text-center">
          <p className="text-[11px] font-medium tracking-[0.18em] text-muted-foreground uppercase">
            Platform 9S
          </p>
          <p className="text-xs text-muted-foreground/80">{t("common.preparingWorkspace")}</p>
        </div>
      </div>
      <span className="sr-only">{t("common.loadingPage")}</span>
    </div>
  );
}

export function ContentLoadingFallback({
  className,
  testId = "workspace-route-pending",
}: {
  className?: string;
  testId?: string;
}) {
  const { t } = useTranslation();
  return (
    <div
      className={cn("flex w-full min-w-0 flex-1 flex-col px-0 py-0", className)}
      role="status"
      data-testid={testId}
    >
      <HubListChromeSkeleton />
      <span className="sr-only">{t("common.loadingContent")}</span>
    </div>
  );
}

export function InlineContentLoading({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <div className={cn("grid w-full gap-2.5 py-4", className)} role="status">
      <div className="flex items-center gap-2">
        <Skeleton className="h-8 w-8 rounded-md" />
        <Skeleton className="h-4 w-40" />
      </div>
      <Skeleton className="h-20 w-full rounded-lg" style={{ animationDelay: "40ms" }} />
      <Skeleton className="h-10 w-full rounded-lg" style={{ animationDelay: "80ms" }} />
      <Skeleton className="h-10 w-[85%] rounded-lg" style={{ animationDelay: "120ms" }} />
      <span className="sr-only">{t("common.loading")}</span>
    </div>
  );
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ size, label, className }) => {
  const { t } = useTranslation();
  const brandSize = size === "sm" ? "sm" : size === "lg" ? "lg" : "md";

  return (
    <div className={cn("flex flex-col items-center justify-center gap-2", className)} role="status">
      <BrandMarkSpinner size={brandSize} />
      {label ? <span className="text-sm text-muted-foreground">{label}</span> : null}
      {!label ? <span className="sr-only">{t("common.loading")}</span> : null}
    </div>
  );
};
